"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import type { Variants } from "framer-motion";
import {
  Calendar,
  ChevronRight,
  ChevronLeft,
  PieChart,
  BarChart,
} from "lucide-react";
import { MonthYearFilter } from "./components/dashboard/MonthYearFilter";
import { FinancialOverviewCards } from "./components/dashboard/FinancialOverviewCards";
import { ReceiptDetailsCard } from "./components/dashboard/ReceiptDetailsCard";
import { TopProductsChart } from "./components/dashboard/TopProductsChart";
import { ExpensesByCategoryChart } from "./components/dashboard/ExpensesByCategoryChart";
import { ProductsProfitChart } from "./components/dashboard/ProductsProfitChart";
import { DailySalesChart } from "./components/dashboard/DailySalesChart";
import { FinancialOverviewChart } from "./components/dashboard/FinancialOverviewChart";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { addMonths } from "date-fns";

export const Dashboard: React.FC = () => {
  const currentDate = new Date();
  const initialDate = `${String(currentDate.getMonth() + 1).padStart(
    2,
    "0"
  )}/${currentDate.getFullYear()}`;

  const [selectedDate, setSelectedDate] = useState<string>(initialDate);
  const [previousDate, setPreviousDate] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [comparisonMode, setComparisonMode] = useState<boolean>(false);

  useEffect(() => {
    // Calcular o mês anterior para comparações
    const [month, year] = selectedDate.split("/").map(Number);
    let prevMonth = month - 1;
    let prevYear = year;

    if (prevMonth === 0) {
      prevMonth = 12;
      prevYear -= 1;
    }

    setPreviousDate(`${String(prevMonth).padStart(2, "0")}/${prevYear}`);
  }, [selectedDate]);

  const handleFilter = (date: string) => {
    setIsLoading(true);
    setSelectedDate(date);

    setTimeout(() => {
      setIsLoading(false);
    }, 800);
  };

  const toggleComparisonMode = () => {
    setComparisonMode(!comparisonMode);
  };

  const navigateMonth = (direction: "next" | "prev") => {
    const [month, year] = selectedDate.split("/").map(Number);
    let newMonth = month;
    let newYear = year;

    if (direction === "next") {
      newMonth += 1;
      if (newMonth > 12) {
        newMonth = 1;
        newYear += 1;
      }
    } else {
      newMonth -= 1;
      if (newMonth < 1) {
        newMonth = 12;
        newYear -= 1;
      }
    }

    handleFilter(`${String(newMonth).padStart(2, "0")}/${newYear}`);
  };

  // Variantes para animações
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 },
    },
  };

  return (
    <div className="container mx-auto p-4 pb-20">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 mb-6 shadow-lg"
      >
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-white">
              Dashboard Financeiro
            </h1>
            <p className="text-orange-100 mt-1">
              {new Date(
                addMonths(
                  `${selectedDate.split("/")[1]}-${
                    selectedDate.split("/")[0]
                  }-01`,
                  1
                )
              ).toLocaleDateString("pt-BR", {
                month: "long",
                year: "numeric",
              })}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                className="bg-white/20 text-white border-white/30 hover:bg-white/30"
                onClick={() => navigateMonth("prev")}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="bg-white/20 text-white border-white/30 hover:bg-white/30"
                onClick={() => navigateMonth("next")}
              >
                Próximo
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            <MonthYearFilter
              onFilter={handleFilter}
              initialMonth={selectedDate.split("/")[0]}
              initialYear={selectedDate.split("/")[1]}
            />
          </div>
        </div>
      </motion.div>

      <div className="flex justify-between items-center mb-6">
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleComparisonMode}
            className={
              comparisonMode
                ? "bg-orange-100 text-orange-700 border-orange-200"
                : ""
            }
          >
            <Calendar className="h-4 w-4 mr-2" />
            {comparisonMode ? "Desativar Comparação" : "Comparar Períodos"}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex justify-between items-center mb-6">
          <TabsList className="grid grid-cols-2 sm:w-[400px]">
            <TabsTrigger value="overview" className="flex items-center">
              <PieChart className="h-4 w-4 mr-2" />
              Visão Geral
            </TabsTrigger>
            <TabsTrigger value="sales" className="flex items-center">
              <BarChart className="h-4 w-4 mr-2" />
              Vendas
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="mt-0">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants}>
              <FinancialOverviewCards
                monthYear={selectedDate}
                previousMonthYear={comparisonMode ? previousDate : undefined}
                isLoading={isLoading}
              />
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6"
            >
              <div className="lg:col-span-2">
                <FinancialOverviewChart
                  monthYear={selectedDate}
                  isLoading={isLoading}
                />
              </div>
              <div>
                <TopProductsChart
                  monthYear={selectedDate}
                  isLoading={isLoading}
                  limit={5}
                />
              </div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6"
            >
              <ReceiptDetailsCard
                monthYear={selectedDate}
                isLoading={isLoading}
              />
              <ExpensesByCategoryChart
                monthYear={selectedDate}
                isLoading={isLoading}
              />
              <DailySalesChart monthYear={selectedDate} isLoading={isLoading} />
            </motion.div>
          </motion.div>
        </TabsContent>

        <TabsContent value="sales" className="mt-0">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6"
            >
              <ProductsProfitChart
                monthYear={selectedDate}
                isLoading={isLoading}
              />
              <TopProductsChart
                monthYear={selectedDate}
                isLoading={isLoading}
                limit={10}
              />
            </motion.div>
          </motion.div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
